import { useState, useEffect } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Brain, Loader2, AlertCircle } from 'lucide-react'
import api from '@/lib/api'
import { useSubdomain } from '@/hooks/useSubdomain'
import { useQueryClient } from '@tanstack/react-query'
import { useTenant } from '@/contexts/AuthContext'

export function Login() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [workspace, setWorkspace] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  const [successMessage, setSuccessMessage] = useState('')
  const navigate = useNavigate()
  const subdomain = useSubdomain()
  const queryClient = useQueryClient()
  const { setTenant } = useTenant()
  
  // Check for post-signup success and pre-populate email and workspace
  useEffect(() => {
    const signupEmail = sessionStorage.getItem('signup_email')
    const signupWorkspace = sessionStorage.getItem('signup_workspace')
    const signupSuccess = sessionStorage.getItem('signup_success')
    
    if (signupSuccess === 'true') {
      if (signupEmail) setEmail(signupEmail)
      if (signupWorkspace) setWorkspace(signupWorkspace)
      setSuccessMessage('Account created successfully! Please log in with your credentials.')
      sessionStorage.removeItem('signup_email')
      sessionStorage.removeItem('signup_workspace')
      sessionStorage.removeItem('signup_success')
    }
  }, [])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    if (!email.trim() || !email.includes('@')) {
      setError('Please enter a valid email address')
      return
    }

    if (!password) {
      setError('Password is required')
      return
    }

    if (!workspace.trim()) {
      setError('Workspace is required')
      return
    }

    setIsLoading(true)

    try {
      const response = await api.post('/auth/login/', {
        email: email.toLowerCase().trim(),
        password,
        workspace: workspace.toLowerCase().trim()
      })

      if (response.data.success) {
        // Store tenant info
        if (response.data.tenant) {
          setTenant(response.data.tenant)
        }
        
        // Invalidate and refetch session query to update authentication state
        await queryClient.invalidateQueries({ queryKey: ['session'] })
        
        // Login successful - redirect to dashboard
        navigate('/app/dashboard')
      } else {
        setError(response.data.error || 'Login failed')
        setIsLoading(false)
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || 'Invalid email or password'
      
      // Special handling for verification required
      if (err.response?.data?.needs_verification) {
        setError('Please verify your email before logging in. Check your inbox for verification link.')
      } else {
        setError(errorMessage)
      }
      
      setIsLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-muted/20 flex flex-col">
      {/* Navigation */}
      <nav className="border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center gap-2">
            <Brain className="h-8 w-8 text-primary" />
            <div>
              <span className="text-xl font-bold">Chatterpillar</span>
              {subdomain && (
                <span className="ml-2 text-sm text-muted-foreground">
                  {subdomain}
                </span>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 p-3 bg-primary/10 rounded-full w-fit">
              <Brain className="h-8 w-8 text-primary" />
            </div>
            <CardTitle className="text-2xl">Welcome back</CardTitle>
            <CardDescription>
              Sign in to your workspace
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {successMessage && (
                <Alert className="bg-green-50 text-green-900 border-green-200">
                  <AlertDescription>{successMessage}</AlertDescription>
                </Alert>
              )}
              
              {error && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <div className="space-y-2">
                <Label htmlFor="workspace">Workspace</Label>
                <Input
                  id="workspace"
                  type="text"
                  placeholder="acme"
                  value={workspace}
                  onChange={(e) => setWorkspace(e.target.value.toLowerCase())}
                  disabled={isLoading}
                  autoFocus={!successMessage}
                  autoComplete="off"
                />
                <p className="text-xs text-muted-foreground">
                  Enter your company workspace identifier
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={isLoading}
                  autoComplete="email"
                />
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password">Password</Label>
                  <button
                    type="button"
                    className="text-xs text-primary hover:underline"
                    onClick={() => {
                      // TODO: Implement forgot password flow
                      alert('Password reset will be implemented in the next phase')
                    }}
                  >
                    Forgot password?
                  </button>
                </div>
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={isLoading}
                  autoComplete="current-password"
                />
              </div>

              <Button type="submit" className="w-full" disabled={isLoading}>
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Signing in...
                  </>
                ) : (
                  'Sign In'
                )}
              </Button>

              <div className="text-center text-sm text-muted-foreground">
                <p>
                  Don't have an account?{' '}
                  <Link to="/signup" className="text-primary hover:underline">
                    Create a workspace
                  </Link>
                </p>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>

      {/* Footer */}
      <footer className="border-t bg-background/95 backdrop-blur py-6">
        <div className="container mx-auto px-4 text-center text-sm text-muted-foreground">
          <p>© 2024 Chatterpillar. All rights reserved.</p>
        </div>
      </footer>
    </div>
  )
}
